import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
const POLL_INTERVAL_MS = 2000;
const DEFAULT_JOB_TIMEOUT_MS = 180_000; // 3 minutes

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function sanitise(value: string): string {
  return value.replace(/<[^>]+>/g, "").trim();
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: { ...headers, ...options.headers },
    });
  } catch {
    throw new Error(
      "Could not reach the server. Please check your network connection and try again."
    );
  }
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error("Not authorised. Please sign in again.");
    }
    if (response.status === 429) {
      throw new Error("Rate limit reached. Please wait a moment and try again.");
    }
    const errorBody = await response.text().catch(() => "");
    try {
      const parsed = JSON.parse(errorBody);
      if (parsed.error) throw new Error(parsed.error);
    } catch {
      /* fall through */
    }
    throw new Error(`API ${response.status}: ${errorBody.slice(0, 200) || response.statusText}`);
  }
  return response.json();
}

export type JobStatus = "pending" | "running" | "completed" | "failed";

export interface CrmProfile {
  user_sub: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  org_name: string;
  org_type: string;
  use_cases: string[];
  hear_about: string;
  goals: string;
  phone: string;
  country: string;
  newsletter: boolean;
  created_at: number;
  updated_at: number;
  source: string;
}

export interface JobProgress {
  status: JobStatus;
  elapsed_ms: number;
  job_id: string;
}

export type ProgressCallback = (progress: JobProgress) => void;

async function submitAsyncJob<T>(
  path: string,
  body: unknown,
  onProgress?: ProgressCallback,
  timeoutMs: number = DEFAULT_JOB_TIMEOUT_MS
): Promise<T> {
  // Phase 1: submit — returns 202 with job_id in <1s
  const submitResponse = await request<{ job_id: string; status: string; poll_url: string }>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });

  const jobId = submitResponse.job_id;
  if (!jobId) {
    throw new Error("Server did not return a job identifier.");
  }

  // Phase 2: poll until done
  const startTime = Date.now();
  while (Date.now() - startTime < timeoutMs) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    const job = await request<{
      job_id: string;
      status: JobStatus;
      result?: T;
      error?: string;
    }>(`/v1/jobs/${jobId}`);

    onProgress?.({ status: job.status, elapsed_ms: Date.now() - startTime, job_id: jobId });

    if (job.status === "completed") {
      if (!job.result) {
        throw new Error("Job completed but returned no result.");
      }
      return job.result;
    }
    if (job.status === "failed") {
      throw new Error(job.error || "The AI model encountered an error processing your request.");
    }
  }

  throw new Error(
    `Job did not complete within ${Math.round(timeoutMs / 1000)} seconds. It may still be running — try again in a minute.`
  );
}

export const api = {
  health: () =>
    request<{ status: string; version: string; dependencies: Record<string, string> }>(
      "/v1/health"
    ),

  // Fast sync endpoints
  vitaminRecommend: (profile: Record<string, unknown>) =>
    request<{
      recommendations: Array<{
        vitamin_id: string;
        name: string;
        score: number;
        confidence: number;
        nhs_rda: string;
        reasoning: string[];
        nhs_reference?: string;
      }>;
      profile_summary: string;
      disclaimer: string;
    }>("/v1/vitamin/recommend", { method: "POST", body: JSON.stringify(profile) }),

  vitaminLookup: (id: string) =>
    request<{ vitamin: Record<string, unknown>; disclaimer: string }>(
      `/v1/vitamin/${encodeURIComponent(sanitise(id))}`
    ),

  // Async LLM endpoints — submit + poll with progress callback
  literatureQuery: (
    query: string,
    filters?: Record<string, string>,
    onProgress?: ProgressCallback
  ) =>
    submitAsyncJob<{
      answer: string;
      confidence: number;
      citations: unknown[];
      disclaimer: string;
    }>(
      "/v1/literature/query",
      { query: sanitise(query), filters },
      onProgress
    ),

  reportGenerate: (
    data: {
      report_type: string;
      input_data: Record<string, unknown>;
      audience: string;
      output_format: string;
    },
    onProgress?: ProgressCallback
  ) =>
    submitAsyncJob<{ report: string; disclaimer: string }>(
      "/v1/report/generate",
      data,
      onProgress
    ),

  regulatoryDraft: (
    data: {
      document_type: string;
      study_synopsis: string;
      indication: string;
      framework: string;
    },
    onProgress?: ProgressCallback
  ) =>
    submitAsyncJob<{ document: string; disclaimer: string }>(
      "/v1/regulatory/draft",
      {
        ...data,
        study_synopsis: sanitise(data.study_synopsis),
        indication: sanitise(data.indication),
      },
      onProgress
    ),

  interpret: (
    data: {
      data_type: string;
      data_points: Array<Record<string, unknown>>;
      audience: string;
    },
    onProgress?: ProgressCallback
  ) =>
    submitAsyncJob<{ interpretation: string; disclaimer: string }>(
      "/v1/interpret",
      data,
      onProgress
    ),

  // ── CRM / Onboarding ──────────────────────────────────────────────────
  crmGetProfile: () =>
    request<{
      profile?: CrmProfile;
      onboarding_complete: boolean;
    }>("/v1/crm/profile"),

  crmSaveProfile: (profile: Partial<CrmProfile>) =>
    request<{ message: string; onboarding_complete: boolean; profile: CrmProfile }>(
      "/v1/crm/profile",
      { method: "POST", body: JSON.stringify(profile) }
    ),

  // Admin only
  crmListProfiles: () =>
    request<{ profiles: CrmProfile[]; count: number }>("/v1/crm/profiles"),

  reportAnalyse: (
    data: {
      report_type: string;
      audience: string;
      notes: string;
      files: Array<{ media_type: string; data: string; filename: string }>;
    },
    onProgress?: ProgressCallback
  ) =>
    submitAsyncJob<{
      analysis: string;
      report_type: string;
      audience: string;
      files_analysed: number;
      disclaimer: string;
    }>("/v1/report/analyse", data, onProgress, 300_000), // 5 min timeout for multi-file
};
